import {
  BlockOutlined,
  DownloadOutlined,
  EllipsisOutlined,
  ExportOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import type { TableColumnsType, UploadFile, UploadProps } from 'antd';
import { Dropdown, Menu, Skeleton, Spin, Tooltip, Upload, message } from 'antd';
import * as dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';
import { createRef, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import orderApi from '../../apis/order';
import shippoApi from '../../apis/shippo';
import storeApi from '../../apis/store';
import userApi from '../../apis/user';
import {
  Country,
  OrderProductTypeMap,
  OrderStatus,
  PRODUCT_TYPE_CHOICE_KEY,
  ProductTypeOptions,
  ShipmentStatus,
  NeedsShippoPurchaseSentinels,
  StatusFilterOptions,
  TrackingFilter,
  LabelFilter,
  isEligibleForShippoPurchase,
  isShippoEligibleOrderType,
} from '../../constants/order';
import { ButtonType } from '../../constants/style';
import { useHasMounted } from '../../hooks';
import {
  IOrder,
  IOrderItemResponse,
  OrderType,
  ProductVariantLabelMap,
  RequestStatus,
  ResendRequestStatusLabelMap,
  SortOption,
} from '../../interfaces/Order';
import { PaginationParamsResponse } from '../../interfaces/PaginationParamsResponse';
import { IUser } from '../../interfaces/UserModel';
import { RootState } from '../../reducers';
import { IAuthState } from '../../reducers/auth';
import { Role } from '../../role/role';
import { FlexContainer, PageContainer } from '../../styles/Global';
import Button from '../Button/Button';
import OrderTypePromptModal from '../Modal/OrderTypePromptModal';
import PageHeader from '../PageHeader/PageHeader';
import {
  ActionContainer,
  BulkActionButton,
  CustomSize,
  DesignWrapper,
  NestedTable,
  NestedTableImage,
  OrderTable,
  SearchFilterContainer,
  SellerDateWrapper,
  SpinContainer,
  StatusTag,
  StyledCountry,
  StyledDatePicker,
  StyledDivider,
  StyledOrderDate,
  StyledOrderId,
  StyledSearch,
  StyledSelect,
  StyledStatusFilterItem,
  StyledStatusFilterItemCountWrapper,
  StyledStatusFilterItemListContainer,
  StyledStatusFilterItemWrapper,
  ThreeDot,
} from './Order.styled';

const OrderList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [itemsPagination, setItemsPagintion] = useState<
    PaginationParamsResponse<IOrder>
  >({
    items: [],
    page: 1,
    perPage: 10,
    total: 0,
    pageCount: 0,
  });
  const { currentUser } = useSelector(
    (state: RootState) => state.auth as IAuthState
  );
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const isAdmin = currentUser?.role === Role.ADMIN;
  const [spinLoading, setSpinLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [shipmentStatus, setShipmentStatus] = useState<ShipmentStatus>();
  const [country, setCountry] = useState('');
  const [product, setProduct] = useState('');
  const [sortFieldValue, setSortFieldValue] = useState('');
  const [sortOrderValue, setSortOrderValue] = useState('');
  const [trackingFilter, setTrackingFilter] = useState<TrackingFilter>();
  const [labelFilter, setLabelFilter] = useState<LabelFilter>();
  const [filterDate, setFilterDate] = useState<{
    startDate: any;
    endDate: any;
  }>();
  const [search, setSearch] = useState('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [sellers, setSellers] = useState<Array<IUser>>();
  const [sellerUsername, setSellerUsername] = useState<Array<string>>();
  const [stores, setStores] = useState<Array<{ id: string; name: string }>>([]);
  const [storeId, setStoreId] = useState<string | undefined>();
  const [orderStatusCount, setOrderStatusCount] =
    useState<{ status: string; count: number }[]>();
  const mounted = useHasMounted();
  const downloadLinkRef = createRef<HTMLAnchorElement>();
  const resendRequestOrdersSelected = useMemo(() => {
    const orders = itemsPagination.items.filter((order) =>
      (selectedRowKeys as Array<string>).includes(order?.id || '')
    );

    return orders.filter((order) => !!order.resendRequest);
  }, [selectedRowKeys, itemsPagination]);
  const canBuySelectedShippoLabels = useMemo(() => {
    if (selectedRowKeys.length === 0) return false;
    const selectedOrders = itemsPagination.items.filter((order) =>
      (selectedRowKeys as Array<string>).includes(order?.id || ''),
    );
    if (selectedOrders.length !== selectedRowKeys.length) return false;
    return selectedOrders.every(isEligibleForShippoPurchase);
  }, [selectedRowKeys, itemsPagination]);
  const [visibleOrderTypeModal, setVisibleOrderTypeModal] = useState(false);

  useEffect(() => {
    if (!mounted()) return;

    if (currentPage !== 1) {
      setCurrentPage(1);
      return;
    }

    searchOrder();
  }, [
    mounted,
    status,
    country,
    search,
    filterDate,
    sortFieldValue,
    sortOrderValue,
    trackingFilter,
    labelFilter,
    product,
    sellerUsername,
    shipmentStatus,
    storeId,
  ]);

  useEffect(() => {
    if (!mounted()) return;

    searchOrder();
    getOrderStatusCount();
  }, [mounted, currentPage, perPage]);

  useEffect(() => {
    if (!mounted()) return;

    if (isAdmin) {
      getSellers();
    } else {
      getStores();
    }
  }, [mounted, isAdmin]);

  useEffect(() => {
    if (!mounted() || !isAdmin) return;
    loadAdminStores();
  }, [mounted, isAdmin, sellerUsername]);

  const loadAdminStores = async () => {
    try {
      if (!sellerUsername?.length || sellerUsername.length !== 1 || !sellers?.length) {
        setStores([]);
        setStoreId(undefined);
        return;
      }
      const seller = sellers.find((s) => s.username === sellerUsername[0]);
      if (!seller?.id) {
        setStores([]);
        return;
      }
      const list = await storeApi.listStoresForSeller(seller.id);
      setStores(list.map((s) => ({ id: s.id, name: s.name })));
    } catch (e) {
      console.error(e);
      setStores([]);
    }
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    columnWidth: 70,
  };

  const deleteOrder = async (id: string) => {
    if (!id) {
      return;
    }

    try {
      setActionLoading(true);
      await orderApi.deleteOrder([id]);
      message.success('Delete order success!');
      // update list
      const tmp = cloneDeep(itemsPagination.items);
      const idx = tmp.findIndex((t) => t.id === id);
      if (idx > -1) {
        tmp.splice(idx, 1);
        setItemsPagintion({
          ...itemsPagination,
          items: tmp,
        });
      }
    } catch (e) {
      message.error('Delete order fail!');
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  const quickActionHandle = async (id: string, status: string) => {
    try {
      setActionLoading(true);
      await orderApi.updateOrderStatus(id, status);
      message.success('Update order status success!');

      // Update list order
      const tmp = cloneDeep(itemsPagination.items);
      const idx = tmp.findIndex((t) => t.id === id);
      if (idx > -1) {
        tmp[idx].status = status as OrderStatus;
        setItemsPagintion({
          ...itemsPagination,
          items: tmp,
        });
      }
    } catch (e) {
      message.error('Update order status fail!');
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  const createShippingLabel = async (id: string) => {
    try {
      setActionLoading(true);
      const response = await orderApi.createShippingLabel(id);
      message.success('Shipping label created successfully!');

      // Update the order with new shipping information
      const tmp = cloneDeep(itemsPagination.items);
      const idx = tmp.findIndex((t) => t.id === id);
      if (idx > -1) {
        tmp[idx] = {
          ...tmp[idx],
          shippingLabelUrl: response.shippingLabelUrl,
          trackingReference: response.trackingReference,
          trackingUrl: response.trackingUrl,
          shippingCarrier: response.shippingCarrier,
          shippingService: response.shippingService,
          estimatedDeliveryDate: response.estimatedDeliveryDate,
          shippingCost: response.shippingCost,
          shipmentId: response.shipmentId,
          shipmentStatus: response.shipmentStatus,
        };
        setItemsPagintion({
          ...itemsPagination,
          items: tmp,
        });
      }
    } catch (e) {
      message.error('Failed to create shipping label');
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  const purchaseShipment = async (id: string, address: string) => {
    try {
      setActionLoading(true);
      const response = await orderApi.purchaseShipment(id, address);
      message.success('Shipment purchased successfully!');

      // Update the order with new shipping information
      const tmp = cloneDeep(itemsPagination.items);
      const idx = tmp.findIndex((t) => t.id === id);
      if (idx > -1) {
        tmp[idx] = {
          ...tmp[idx],
          shippingLabelUrl: response.shippingLabelUrl,
          trackingReference: response.trackingReference,
          trackingUrl: response.trackingUrl,
          shippingCarrier: response.shippingCarrier,
          shippingService: response.shippingService,
          estimatedDeliveryDate: response.estimatedDeliveryDate,
          shippingCost: response.shippingCost,
          shipmentId: response.shipmentId,
          shipmentStatus: response.shipmentStatus,
        };
        setItemsPagintion({
          ...itemsPagination,
          items: tmp,
        });
      }
    } catch (e) {
      message.error('Failed to purchase shipment');
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  const purchaseShippoShipment = async (id: string, address: string) => {
    try {
      setActionLoading(true);
      const response = await orderApi.purchaseShippoShipment(id, address);
      message.success('Shippo label purchased successfully!');

      const tmp = cloneDeep(itemsPagination.items);
      const idx = tmp.findIndex((t) => t.id === id);
      if (idx > -1) {
        tmp[idx] = {
          ...tmp[idx],
          shippingLabelUrl: response.shippingLabelUrl,
          trackingReference: response.trackingReference,
          trackingUrl: response.trackingUrl,
          shippingCarrier: response.shippingCarrier,
          shippingService: response.shippingService,
          estimatedDeliveryDate: response.estimatedDeliveryDate,
          shippingCost: response.shippingCost,
          labelFee: response.labelFee,
          labelProvider: response.labelProvider,
          shipmentId: response.shipmentId,
          shipmentStatus: response.shipmentStatus,
          status: response.status || tmp[idx].status,
          totalCost: response.totalCost,
          shouldLockTotalCost: response.shouldLockTotalCost,
        };
        setItemsPagintion({
          ...itemsPagination,
          items: tmp,
        });
      }
    } catch (e: any) {
      message.error(
        e?.response?.data?.message || 'Failed to purchase Shippo label',
      );
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  // Link a dashboard-bought Shippo label to this order by its pasted tracking number.
  const syncShippoOrder = async (id: string) => {
    try {
      setActionLoading(true);
      await shippoApi.syncOrder(id);
      message.success('Shippo label linked to the order');
      await searchOrder();
    } catch (e: any) {
      message.error(
        e?.response?.data?.message || 'Failed to sync with Shippo',
      );
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  const getOrderStatusCount = async () => {
    try {
      const res = await orderApi.getOrderStatusCount();
      console.log(res);
      setOrderStatusCount(res);
    } catch (error) {
      console.error(error);
    }
  };

  const getSellers = async () => {
    try {
      const sellers = await userApi.listUsers({ all: true });
      setSellers(sellers.items);
    } catch (e) {
      message.error('Get sellers fail!');
      console.error(e);
    }
  };

  const getStores = async () => {
    try {
      if (isAdmin) {
        return;
      }
      const list = await storeApi.listStores();
      setStores(list.map((s) => ({ id: s.id, name: s.name })));
    } catch (e) {
      console.error(e);
    }
  };

  const renderStatusFilterItem = (filterStatusOption: {
    label: string;
    value: string;
  }) => {
    const { value, label } = filterStatusOption;
    const isActive = status === value;
    return (
      <StyledStatusFilterItemWrapper $active={isActive}>
        <StyledStatusFilterItem
          $active={isActive}
          onClick={() => setStatus(value)}
        >
          {label}
        </StyledStatusFilterItem>
        {value !== '' && (
          <StyledStatusFilterItemCountWrapper $active={isActive}>
            <StyledStatusFilterItem $active={isActive}>
              {orderStatusCount
                ?.find((item) => item.status === value)
                ?.count.toLocaleString('en-US') || 0}
            </StyledStatusFilterItem>
          </StyledStatusFilterItemCountWrapper>
        )}
      </StyledStatusFilterItemWrapper>
    );
  };

  const expandedRowRender = (row: IOrder) => {
    const columns: TableColumnsType<IOrderItemResponse> = [
      {
        title: 'Image',
        dataIndex: 'design',
        key: 'designUrl',
        render: (_: any, order: IOrderItemResponse) =>
          order?.design?.isGeneratingThumbnail ? (
            <Skeleton.Image active style={{ width: 100, height: 50 }} />
          ) : (
            <NestedTableImage
              src={
                order?.design?.thumbnailUrl ??
                order?.design?.originalFileUrl ??
                order?.design?.designUrl
              }
            />
          ),
        width: 200,
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
        key: 'quantity',
        render: (_: any, order: IOrderItemResponse) => <p>{order?.quantity}</p>,
        width: 140,
      },
      {
        title: 'Size',
        dataIndex: 'size',
        key: 'size',
        render: (_: any, order: IOrderItemResponse) => (
          <CustomSize
            $2in={order?.size?.size === '2in'}
            $3in={order?.size?.size === '3in'}
            $4in={order?.size?.size === '4in'}
            $5in={order?.size?.size === '5in'}
            $6in={order?.size?.size === '6in'}
            $card={order?.size?.size === 'Card'}
          >
            {order?.size?.size}
          </CustomSize>
        ),
        width: 140,
      },
      {
        title: 'Variant',
        dataIndex: 'variant',
        key: 'variant',
        render: (_: any, order: IOrderItemResponse) => (
          <p>
            {order?.variant ? ProductVariantLabelMap[order.variant] : '-'}
          </p>
        ),
        width: 120,
      },
      {
        title: 'Sku',
        dataIndex: 'design',
        key: 'sku',
        render: (_: any, order: IOrderItemResponse) => (
          <p>{order?.design?.sku}</p>
        ),
        width: 140,
      },
      {
        title: 'Cost',
        dataIndex: 'size',
        key: 'price',
        render: (_: any, order: IOrderItemResponse) => (
          <p>${order?.itemCost?.toLocaleString('en-US')}</p>
        ),
        width: 120,
      },
    ];

    const data = row.orderDesigns;

    return (
      <NestedTable
        className="nested-table"
        columns={columns as any}
        dataSource={data}
        pagination={false}
        showHeader={true}
      />
    );
  };

  const columns = [
    {
      title: 'Order ID',
      key: 'orderId',
      editable: false,
      dataIndex: 'orderId',
      render: (_: any, order: IOrder) => (
        <Tooltip title={order?.orderId}>
          <div className="ellipsis" style={{ maxWidth: 160 }}>
            <SellerDateWrapper>
              <Link
                to={`/order/${order?.id}/edit/${
                  OrderProductTypeMap[order.type ?? OrderType.Sticker]
                }`}
              >
                <StyledOrderId>{order?.orderId}</StyledOrderId>
              </Link>
              <StyledOrderDate>
                {dayjs.default(order?.createdAt).format('DD MMM YYYY')}
              </StyledOrderDate>
            </SellerDateWrapper>
          </div>
        </Tooltip>
      ),
      width: 183,
    },
    ...(isAdmin
      ? [
          {
            title: 'Seller',
            key: 'createdAt',
            editable: false,
            dataIndex: 'createdAt',
            sorter: true,
            showSorterTooltip: false,
            render: (_: any, order: IOrder) => <p>{order?.seller?.username}</p>,
            width: 120,
          },
        ]
      : []),
    {
      title: 'Store',
      key: 'store',
      editable: false,
      dataIndex: 'store',
      render: (_: any, order: IOrder) => <p>{order?.store?.name || '—'}</p>,
      width: 120,
    },
    {
      title: 'Product',
      key: 'type',
      editable: false,
      dataIndex: 'type',
      render: (_: any, order: IOrder) => (
        <Tooltip title={order?.type ?? OrderType.Sticker}>
          <div className="ellipsis" style={{ maxWidth: 160 }}>
            <p>{order?.type ?? OrderType.Sticker}</p>
          </div>
        </Tooltip>
      ),
      width: 120,
    },
    {
      title: 'Designs',
      key: 'designs',
      editable: false,
      dataIndex: 'designs',
      width: 140,
      render: (_: any, order: IOrder) => (
        <DesignWrapper $totalItems={order.orderDesigns?.length || 0}>
          {order.orderDesigns?.slice(0, 4)?.map((od, idx) =>
            od.design?.isGeneratingThumbnail ? (
              <Skeleton.Image
                active
                style={{
                  width: (order.orderDesigns?.length ?? 0) <= 3 ? 60 : 40,
                  height: (order.orderDesigns?.length ?? 0) <= 3 ? 60 : 40,
                }}
              />
            ) : (
              <img
                key={idx}
                src={
                  od.design?.thumbnailUrl ??
                  od.design?.originalFileUrl ??
                  od.design?.designUrl
                }
                alt={od.design?.sku}
              />
            )
          )}
          {!!order.orderDesigns && order.orderDesigns?.length >= 5 && (
            <ThreeDot>...</ThreeDot>
          )}
        </DesignWrapper>
      ),
    },
    {
      title: 'Shipment Status',
      key: 'shipmentStatus',
      editable: false,
      dataIndex: 'shipmentStatus',
      width: 150,
      filters: [
        {
          text: 'Pre Transit',
          value: ShipmentStatus.PRE_TRANSIT,
        },
        {
          text: 'In Transit',
          value: ShipmentStatus.IN_TRANSIT,
        },
        {
          text: 'Out for Delivery',
          value: ShipmentStatus.OUT_FOR_DELIVERY,
        },
        {
          text: 'Delivered',
          value: ShipmentStatus.DELIVERED,
        },
        {
          text: 'Available for Pickup',
          value: ShipmentStatus.AVAILABLE_FOR_PICKUP,
        },
        {
          text: 'Return to Sender',
          value: ShipmentStatus.RETURN_TO_SENDER,
        },
        {
          text: 'Failure',
          value: ShipmentStatus.FAILURE,
        },
        {
          text: 'Cancelled',
          value: ShipmentStatus.CANCELLED,
        },
        {
          text: 'Unknown',
          value: ShipmentStatus.UNKNOWN,
        },
      ],
      filterMultiple: false,
      onFilter: (value, record) => {
        return record.shipmentStatus === value;
      },
      render: (_: any, order: IOrder) => (
        <div>{order?.shipmentStatus || ''}</div>
      ),
    },
    {
      title: 'Address',
      key: 'address',
      editable: false,
      dataIndex: 'address',
      width: 315,
      render: (_: any, order: IOrder) => (
        <div style={{ whiteSpace: 'pre-wrap' }}>{order?.address}</div>
      ),
    },
    {
      title: 'Tracking',
      key: 'trackingReference',
      editable: false,
      dataIndex: 'trackingReference',
      width: 315,
      filters: [
        {
          text: 'No Tracking',
          value: TrackingFilter.NoTracking,
        },
        {
          text: 'Tracking only',
          value: TrackingFilter.TrackingOnly,
        },
        {
          text: 'Đã mua shipment',
          value: TrackingFilter.ShipmentOnly,
        },
        {
          text: 'Cần Tracking',
          value: TrackingFilter.NeedsShippoPurchase,
        },
      ],
      filterMultiple: false,
      onFilter: () => {
        return true;
      },
      render: (_: any, order: IOrder) => (
        <Tooltip trigger={['hover']} title={order?.trackingReference}>
          <div className="ellipsis">
            {order?.trackingUrl ? (
              <a
                href={order.trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {order?.trackingReference}
              </a>
            ) : (
              order?.trackingReference
            )}
          </div>
        </Tooltip>
      ),
    },
    {
      // Visible to sellers too: the Shippo label fee is charged to them
      // (baked into totalCost), so it is shown for transparency.
      title: 'Label Fee',
      key: 'labelFee',
      editable: false,
      dataIndex: 'labelFee',
      width: 130,
      render: (_: any, order: IOrder) => (
        <p>
          {order?.labelFee != null
            ? `$${Number(order.labelFee).toFixed(2)}`
            : '—'}
        </p>
      ),
    },
    {
      title: 'Shipping Service',
      key: 'shippingService',
      editable: false,
      dataIndex: 'shippingService',
      width: 130,
      render: (_: any, order: IOrder) => (
        <div>
          {(order?.shippingCarrier || order?.shippingService) && (
            <Tooltip
              title={`${order?.shippingCarrier || ''} ${
                order?.shippingService ? '- ' + order?.shippingService : ''
              }`}
            >
              <div>
                {order?.shippingCarrier}{' '}
                {order?.shippingService && `- ${order?.shippingService}`}
              </div>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Country',
      key: 'country',
      editable: false,
      dataIndex: 'country',
      width: 120,
      render: (_: any, order: any) => (
        <StyledCountry $cad={order?.country === Country.CANADA}>
          {order?.country === Country.CANADA ? 'CANADA' : 'US'}
        </StyledCountry>
      ),
    },
    {
      title: 'Est. Delivery',
      key: 'estimatedDeliveryDate',
      editable: false,
      dataIndex: 'estimatedDeliveryDate',
      width: 140,
      render: (_: any, order: IOrder) => (
        <p>
          {order?.estimatedDeliveryDate
            ? dayjs.default(order?.estimatedDeliveryDate).format('DD MMM YYYY')
            : ''}
        </p>
      ),
    },
    {
      title: 'Cost',
      key: 'totalCost',
      editable: false,
      dataIndex: 'totalCost',
      sorter: true,
      showSorterTooltip: false,
      width: 120,
      render: (_: any, order: IOrder) => (
        <p>
          $
          {order.status === OrderStatus.CANCELLED
            ? 0
            : order?.totalCost?.toLocaleString('en-US')}
        </p>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      editable: false,
      dataIndex: 'status',
      width: 160,
      render: (_: any, order: IOrder) => {
        return !!order?.resendRequest ? (
          <StatusTag
            $pendingResend={
              order?.resendRequest.status === RequestStatus.PENDING
            }
            $approvedResend={
              order?.resendRequest.status === RequestStatus.APPROVED
            }
            $rejected={order?.resendRequest.status === RequestStatus.REJECTED}
            $fontSize={12}
          >
            <span style={{ fontSize: '20px' }}>•</span>{' '}
            {ResendRequestStatusLabelMap[order?.resendRequest?.status]}
          </StatusTag>
        ) : (
          <StatusTag
            $pending={order?.status === OrderStatus.PENDING}
            $process={order?.status === OrderStatus.PROCESSING}
            $shipped={order?.status === OrderStatus.SHIPPED}
            $reshipped={order?.status === OrderStatus.RESHIPPED}
            $cancel={order?.status === OrderStatus.CANCELLED}
            $fontSize={12}
          >
            <span style={{ fontSize: '20px' }}>•</span> {order?.status}
          </StatusTag>
        );
      },
    },
    {
      key: 'action',
      editable: false,
      dataIndex: 'id',
      width: 72,
      fixed: 'right',
      render: (_: any, order: any) => (
        <Dropdown
          disabled={actionLoading}
          trigger={['hover']}
          overlay={menuQuickAction(order)}
        >
          <ActionContainer>
            <EllipsisOutlined style={{ fontSize: '20px' }} />
          </ActionContainer>
        </Dropdown>
      ),
    },
  ];

  const menuQuickAction = (order: IOrder) => (
    <Menu style={{ marginBottom: '12px' }}>
      {isAdmin && order.status === OrderStatus.PENDING && (
        <Menu.Item
          key="process"
          onClick={() =>
            order.id && quickActionHandle(order.id, OrderStatus.PROCESSING)
          }
        >
          Process
        </Menu.Item>
      )}
      {isAdmin &&
        !!order.status &&
        [OrderStatus.PROCESSING, OrderStatus.RESHIPPED].includes(
          order.status as OrderStatus
        ) && (
          <Menu.Item
            key="finish"
            onClick={() =>
              order.id && quickActionHandle(order.id, OrderStatus.SHIPPED)
            }
          >
            Finish
          </Menu.Item>
        )}
      {isAdmin && order.status !== OrderStatus.CANCELLED && (
        <Menu.Item
          key="cancel"
          onClick={() =>
            order.id && quickActionHandle(order.id, OrderStatus.CANCELLED)
          }
        >
          Cancel
        </Menu.Item>
      )}
      {isAdmin && order.status === OrderStatus.SHIPPED && (
        <Menu.Item
          key="reship"
          onClick={() =>
            order.id && quickActionHandle(order.id, OrderStatus.RESHIPPED)
          }
        >
          Reship
        </Menu.Item>
      )}
      {isAdmin &&
        !order.trackingReference &&
        order.type !== OrderType.Ornament && (
        <Menu.Item
          key="create-label"
          onClick={() => order.id && createShippingLabel(order.id)}
        >
          Create Shipping Label
        </Menu.Item>
      )}
      {isAdmin &&
        !order.trackingReference &&
        order.type !== OrderType.Ornament && (
        <Menu.Item
          key="purchase-shipment"
          onClick={() => {
            if (order.id && order.address) {
              purchaseShipment(order.id, order.address);
            } else {
              message.error(
                'Order address is required for purchasing shipment'
              );
            }
          }}
        >
          Purchase Shipment
        </Menu.Item>
      )}
      {isAdmin &&
        isShippoEligibleOrderType(order.type) &&
        order.country === Country.US &&
        !order.shipmentId &&
        !!order.trackingReference &&
        !(NeedsShippoPurchaseSentinels as readonly string[]).includes(
          order.trackingReference,
        ) && (
          <Menu.Item
            key="sync-shippo"
            onClick={() => {
              if (order.id) {
                syncShippoOrder(order.id);
              }
            }}
          >
            Sync Shippo
          </Menu.Item>
        )}
      {isAdmin && isEligibleForShippoPurchase(order) && (
          <Menu.Item
            key="purchase-shippo"
            onClick={() => {
              if (order.id && order.address) {
                purchaseShippoShipment(order.id, order.address);
              } else {
                message.error(
                  'Order address is required for purchasing Shippo label',
                );
              }
            }}
          >
            Buy Shippo Label
          </Menu.Item>
        )}
      {!!order.shippingLabelUrl && (
        <Menu.Item key="view-label">
          <a
            href={order.shippingLabelUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            View Shipping Label
          </a>
        </Menu.Item>
      )}
      <Menu.Item key="edit">
        <Link
          to={`/order/${order?.id}/edit/${
            OrderProductTypeMap[order.type ?? OrderType.Sticker]
          }`}
        >
          Edit
        </Link>
      </Menu.Item>
      {!!order.status &&
        [OrderStatus.PENDING, OrderStatus.CANCELLED].includes(
          order.status as OrderStatus
        ) && (
          <Menu.Item
            key="delete"
            onClick={() => order.id && deleteOrder(order.id)}
          >
            Delete
          </Menu.Item>
        )}
    </Menu>
  );

  const menuBulkAction = () => (
    <Menu style={{ marginBottom: '12px' }}>
      {resendRequestOrdersSelected.length > 0 && (
        <Menu.Item
          key="approve-request"
          onClick={() => bulkActionResendRequestHandle(RequestStatus.APPROVED)}
        >
          Approve Resend Requests
        </Menu.Item>
      )}
      {resendRequestOrdersSelected.length > 0 && (
        <Menu.Item
          key="reject-request"
          onClick={() => bulkActionResendRequestHandle(RequestStatus.REJECTED)}
        >
          Reject Resend Requests
        </Menu.Item>
      )}
      <Menu.Item
        key="pending"
        onClick={() => bulkActionHandle(OrderStatus.PENDING)}
      >
        Change to Pending
      </Menu.Item>
      <Menu.Item
        key="process"
        onClick={() => bulkActionHandle(OrderStatus.PROCESSING)}
      >
        Change to Process
      </Menu.Item>
      <Menu.Item
        key="reshipped"
        onClick={() => bulkActionHandle(OrderStatus.RESHIPPED)}
      >
        Change to Reshipped
      </Menu.Item>
      <Menu.Item
        key="shipped"
        onClick={() => bulkActionHandle(OrderStatus.SHIPPED)}
      >
        Change to Shipped
      </Menu.Item>
      <Menu.Item
        key="cancel"
        onClick={() => bulkActionHandle(OrderStatus.CANCELLED)}
      >
        Change to Cancel
      </Menu.Item>
      <Menu.Item
        key="buy-shippo"
        disabled={!canBuySelectedShippoLabels}
        onClick={() => bulkPurchaseShippoLabels()}
      >
        Buy Shippo Labels
      </Menu.Item>
      <Menu.Item disabled key="download">
        Download
      </Menu.Item>
    </Menu>
  );

  const bulkActionHandle = async (status = OrderStatus.PENDING) => {
    if (!status || selectedRowKeys.length === 0) {
      return;
    }

    try {
      setBulkActionLoading(true);
      await orderApi.bulkUpdateOrderStatus(selectedRowKeys as string[], status);

      // Update list order
      const tmp = cloneDeep(itemsPagination.items);
      selectedRowKeys.forEach((id) => {
        const idx = tmp.findIndex((t) => t.id === id);
        if (idx > -1) {
          tmp[idx].status = status;
        }
      });
      setItemsPagintion({
        ...itemsPagination,
        items: tmp,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const bulkPurchaseShippoLabels = async () => {
    if (selectedRowKeys.length === 0) {
      return;
    }

    if (!canBuySelectedShippoLabels) {
      message.warning(
        'Select only Cần Tracking sticker/magnet/ornament orders that do not already have a label',
      );
      return;
    }

    const eligibleIds = selectedRowKeys as string[];

    if (eligibleIds.length > 50) {
      message.warning('You can buy at most 50 Shippo labels at once');
      return;
    }

    try {
      setBulkActionLoading(true);
      const result = await orderApi.bulkPurchaseShippoShipment(eligibleIds);
      const successCount = result.succeeded?.length || 0;
      const failCount = result.failed?.length || 0;

      if (successCount > 0 && failCount === 0) {
        message.success(`Purchased ${successCount} Shippo label(s)`);
      } else if (successCount > 0) {
        message.warning(
          `Purchased ${successCount} label(s); ${failCount} failed`,
        );
        console.warn('Bulk Shippo failures', result.failed);
      } else {
        message.error(
          result.failed?.[0]?.error || 'Failed to purchase Shippo labels',
        );
      }

      setSelectedRowKeys([]);
      const tmp = cloneDeep(itemsPagination.items);
      (result.succeeded || []).forEach((updated) => {
        const idx = tmp.findIndex((t) => t.id === updated.id);
        if (idx > -1) {
          tmp[idx] = { ...tmp[idx], ...updated };
        }
      });
      setItemsPagintion({ ...itemsPagination, items: tmp });
    } catch (e: any) {
      message.error(
        e?.response?.data?.message || 'Failed to bulk purchase Shippo labels',
      );
      console.error(e);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const bulkActionResendRequestHandle = async (status: RequestStatus) => {
    if (!status || selectedRowKeys.length === 0) {
      return;
    }

    try {
      setBulkActionLoading(true);
      await orderApi.bulkUpdateResendRequestStatus(
        resendRequestOrdersSelected.map(
          (order) => order.resendRequest?.id || ''
        ),
        status
      );

      // Update list order
      const tmp = cloneDeep(itemsPagination.items);
      resendRequestOrdersSelected.forEach((order) => {
        const idx = tmp.findIndex((t) => t.id === order.id);
        if (idx > -1) {
          if (!!tmp[idx].resendRequest) {
            tmp[idx].resendRequest!.status = status;
          }
        }
      });
      setItemsPagintion({
        ...itemsPagination,
        items: tmp,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const searchOrder = async () => {
    try {
      setSpinLoading(true);
      let query = {
        country,
        status,
        fromDate: filterDate?.startDate,
        toDate: filterDate?.endDate,
        tracking: trackingFilter,
        labelFilter,
        product,
        seller: sellerUsername,
        shipmentStatus,
        storeId,
      };
      if (sortFieldValue) {
        query[sortFieldValue] = sortOrderValue;
      }
      if (search) {
        (query as any).search = search;
      }

      const res = await orderApi.getOrders(query, {
        page: currentPage,
        perPage,
      });
      setItemsPagintion(res);
    } catch (error) {
      setItemsPagintion({
        items: [],
        page: 1,
        perPage,
        total: 0,
        pageCount: 0,
      });
      console.error(error);
    } finally {
      setSpinLoading(false);
    }
  };

  const loadData = async (
    page = itemsPagination?.page,
    perPage = itemsPagination?.perPage
  ) => {
    try {
      let query = {};
      if (sortFieldValue && sortOrderValue) {
        query[sortFieldValue] = sortOrderValue;
      }
      const res = await orderApi.getOrders(query, { page, perPage });

      setItemsPagintion(res);
    } catch (error) {
      setItemsPagintion({
        items: [],
        page,
        perPage,
        total: 0,
        pageCount: 0,
      });
      console.error(error);
    } finally {
    }
  };

  const onStateChanges = async (page: number, perPage: number) => {
    navigate(`${location.pathname}?page=${page}&perPage=${perPage}`, {
      replace: true,
    });
  };

  const onPageChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setPerPage(pageSize);
    onStateChanges(page, pageSize);
  };

  const handleEventRow = (record: any) => {
    const order = record as IOrder;
    return {
      onClick: (e: any) => {
        e.stopPropagation();

        navigate(
          `/order/${order.id}/edit/${
            OrderProductTypeMap[order.type ?? OrderType.Sticker]
          }`
        );
      },
    };
  };

  const handleChange = (_pagination: any, _filters: any, sorter: any) => {
    // handle sort
    if (sorter) {
      if (sorter.column) {
        let sorterField = sorter.field;
        switch (sorter.field) {
          case 'createdAt':
            sorterField = 'byDate';
            break;
          case 'totalCost':
            sorterField = 'byTotalCost';
            break;
          case 'trackingReference':
            sorterField = 'byTracking';
            break;
        }
        const sorterOrder =
          sorter.order &&
          (sorter.order === 'ascend' ? SortOption.ASC : SortOption.DESC);
        setSortFieldValue(sorterField);
        setSortOrderValue(sorterOrder);
      } else {
        setSortFieldValue('');
        setSortOrderValue('');
      }
    }

    if (_filters) {
      const trackingValue = _filters['trackingReference']?.[0];
      setTrackingFilter(trackingValue ? trackingValue : undefined);

      const shipmentStatusValue = _filters['shipmentStatus']?.[0];
      setShipmentStatus(shipmentStatusValue ? shipmentStatusValue : undefined);
    }
  };

  const handleExportCsv = async () => {
    try {
      setSpinLoading(true);
      let query = {
        country,
        status,
        fromDate: filterDate?.startDate,
        toDate: filterDate?.endDate,
        tracking: trackingFilter,
        labelFilter,
        product,
        seller: sellerUsername,
        shipmentStatus,
      };

      if (sortFieldValue) {
        query[sortFieldValue] = sortOrderValue;
      }

      if (search) {
        (query as any).search = search;
      }

      const data = await orderApi.exportOrdersCsv(query, {
        page: currentPage,
        perPage: perPage,
      });

      const url = window.URL.createObjectURL(new Blob([data]));
      const tmpLink = document.createElement('a');
      tmpLink.href = url;
      const fileName = `HD Sticker ${new Date().toDateString()}-data.csv`;
      tmpLink.setAttribute('download', fileName);
      document.body.appendChild(tmpLink);
      tmpLink.click();
      tmpLink.remove();
    } catch (error) {
      console.error(error);
      message.error('Sorry an error occurred while processing your request!');
    } finally {
      setSpinLoading(false);
    }
  };

  const handleOpenCsvFile: UploadProps['onChange'] = (file: any) => {
    setFileList(
      file?.fileList.length
        ? [
            {
              ...file.fileList[0],
              status:
                file.fileList[0].status === 'error'
                  ? 'done'
                  : file.fileList[0].status,
            },
          ]
        : file?.fileList
    );
  };

  const hanldeUploadOrders = async () => {
    try {
      setSpinLoading(true);

      const formData = new FormData();
      formData.append('dataFile', fileList[0]?.originFileObj || '');

      await orderApi.importOrderCsv(formData);
    } catch (error: any) {
      console.error(error);
      // The API lists every duplicate order (with its CSV row), so keep the
      // toast up long enough to read it.
      message.error(
        error?.message ||
          'Sorry an error occurred while processing your file!',
        10,
      );
    } finally {
      await searchOrder();
      setSpinLoading(false);
      setFileList([]);
    }
  };

  const handleBulkUpdateTracking = async () => {
    try {
      setSpinLoading(true);

      const formData = new FormData();
      formData.append('dataFile', fileList[0]?.originFileObj || '');

      await orderApi.bulkUpdateTracking(formData);
    } catch (error) {
      console.error(error);
      message.error('Sorry an error occurred while processing your file!');
    } finally {
      await searchOrder();
      setSpinLoading(false);
      setFileList([]);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Orders"
        customBlock={
          <FlexContainer>
            {isAdmin && (
              <BulkActionButton
                className="m__r--10"
                $disabled={bulkActionLoading || selectedRowKeys.length === 0}
                disabled={bulkActionLoading || selectedRowKeys.length === 0}
                trigger={['hover']}
                overlay={menuBulkAction()}
              >
                <div>
                  <BlockOutlined size={16} color="#344054" />
                  <span className="m__l--8">Bulk Action</span>
                </div>
              </BulkActionButton>
            )}
            {fileList.length > 0 && (
              <>
                <Button
                  className="font-weight--500 m__r--10"
                  key="4"
                  onClick={handleBulkUpdateTracking}
                >
                  Update Tracking
                </Button>
                <Button
                  className="font-weight--500 m__r--10"
                  key="4"
                  onClick={hanldeUploadOrders}
                >
                  Upload
                </Button>
              </>
            )}
            <Upload
              name="file"
              onChange={handleOpenCsvFile}
              multiple={false}
              fileList={fileList}
              accept=".csv"
            >
              {fileList.length < 1 && (
                <Button
                  className="font-weight--500 m__r--10"
                  key="3"
                  type={ButtonType.OUTLINE}
                >
                  <ExportOutlined size={16} color="#344054" />
                  Open CSV File
                </Button>
              )}
            </Upload>
            <a ref={downloadLinkRef}>
              <Button
                className="font-weight--500 m__r--10"
                key="2"
                onClick={handleExportCsv}
                type={ButtonType.OUTLINE}
              >
                <DownloadOutlined size={16} color="#344054" />
                Export CSV
              </Button>
            </a>
            <Button
              className="font-weight--500"
              key="3"
              onClick={() => {
                const cachedChoiceSerialized = localStorage.getItem(
                  PRODUCT_TYPE_CHOICE_KEY
                );

                if (!!cachedChoiceSerialized) {
                  const cachedChoice = JSON.parse(cachedChoiceSerialized);
                  navigate(
                    `/order/create/${OrderProductTypeMap[cachedChoice]}`
                  );
                } else {
                  setVisibleOrderTypeModal(true);
                }
              }}
            >
              <PlusOutlined size={16} color="#344054" />
              Create Order
            </Button>
          </FlexContainer>
        }
      />
      <SearchFilterContainer $isAdmin={isAdmin} className="m__t--10">
        <StyledSearch placeholder="Search" onSearch={(val) => setSearch(val)} />
        <StyledDatePicker
          onChange={(val) => {
            if (Array.isArray(val) && val.length > 1) {
              setFilterDate({
                startDate: dayjs.default(val[0]).toISOString(),
                endDate: dayjs.default(val[1]).toISOString(),
              });
            } else {
              setFilterDate({
                startDate: null,
                endDate: null,
              });
            }
          }}
          allowClear
        />
        <StyledDivider type="vertical" />
        {isAdmin && (
          <StyledSelect
            className="w-100"
            placeholder="Seller"
            // value={types}
            onChange={(val: any) => {
              setSellerUsername(val);
            }}
            options={sellers?.map((seller) => ({
              label: seller.username,
              value: seller.username,
            }))}
            mode="multiple"
          />
        )}
        <StyledSelect
          className="w-100"
          placeholder="Store"
          allowClear
          onChange={(val: any) => {
            setStoreId(val || undefined);
          }}
          options={[
            { label: 'All stores', value: '' },
            { label: 'Unassigned', value: 'unassigned' },
            ...stores.map((store) => ({
              label: store.name,
              value: store.id,
            })),
          ]}
          value={storeId}
        />
        <StyledSelect
          className="w-100"
          placeholder="Product"
          // value={types}
          onChange={(val: any) => {
            setProduct(val);
          }}
          options={ProductTypeOptions}
        />
        <StyledSelect
          className="w-100"
          placeholder="country"
          // value={types}
          onChange={(val: any) => {
            setCountry(val);
          }}
          options={[
            { label: 'View All', value: '' },
            { label: 'US', value: Country.US },
            { label: 'CANADA', value: Country.CANADA },
          ]}
        />
        <StyledSelect
          className="w-100"
          placeholder="status"
          // value={types}
          onChange={(val: any) => {
            setStatus(val);
          }}
          options={StatusFilterOptions}
          value={status}
        />
        {isAdmin && (
          <StyledSelect
            className="w-100"
            placeholder="Label"
            allowClear
            onChange={(val: any) => {
              setLabelFilter(val || undefined);
            }}
            options={[
              { label: 'View All', value: '' },
              { label: 'Chờ mua Shippo', value: LabelFilter.ShippoPending },
              { label: 'Đã mua Shippo', value: LabelFilter.ShippoBought },
              { label: 'Đã mua EasyPost', value: LabelFilter.EasypostBought },
            ]}
            value={labelFilter}
          />
        )}
      </SearchFilterContainer>
      <StyledStatusFilterItemListContainer>
        {StatusFilterOptions.map((status) => renderStatusFilterItem(status))}
      </StyledStatusFilterItemListContainer>
      <OrderTable
        columns={columns as any}
        dataSource={itemsPagination.items}
        onChange={handleChange}
        scroll={{ x: 1800 }}
        pagination={{
          onChange: onPageChange,
          current: currentPage,
          total: itemsPagination.total,
          pageSize: itemsPagination.perPage,
          showSizeChanger: true,
          pageSizeOptions: [10, 50, 100],
        }}
        expandable={{
          expandedRowRender,
          defaultExpandAllRows: true,
          defaultExpandedRowKeys: ['0'],
          expandIcon: ({ expanded, onExpand, record }) =>
            expanded ? (
              <MinusCircleOutlined
                onClick={(e) => onExpand(record, e)}
                style={{ cursor: 'pointer', fontSize: '18px' }}
              />
            ) : (
              <PlusCircleOutlined
                onClick={(e) => onExpand(record, e)}
                style={{ cursor: 'pointer', fontSize: '18px' }}
              />
            ),
        }}
        rowSelection={isAdmin ? rowSelection : undefined}
        rowKey={(record) => record?.id}
        bordered={false}
      />
      {spinLoading && (
        <SpinContainer>
          <Spin size="large" />
        </SpinContainer>
      )}
      <OrderTypePromptModal
        visible={visibleOrderTypeModal}
        onCancel={() => setVisibleOrderTypeModal(false)}
        onSelectType={(type) =>
          navigate(`/order/create/${OrderProductTypeMap[type]}`)
        }
      />
    </PageContainer>
  );
};

export default OrderList;
